import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppRole } from '@disenchantment/shared';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { GuestRoute } from '@/features/auth/GuestRoute';
import { RoleRoute } from '@/features/auth/RoleRoute';
import { PageSpinner } from '@/components/PageSpinner';

const HomePage = lazy(() => import('@/pages/HomePage'));
const ReleasesPublicPage = lazy(() => import('@/pages/ReleasesPublicPage'));
const AuthLayout = lazy(() =>
  import('@/layouts/AuthLayout').then((m) => ({ default: m.AuthLayout })),
);
const PortalShell = lazy(() =>
  import('@/layouts/PortalShell').then((m) => ({ default: m.PortalShell })),
);
const OverviewPage = lazy(() => import('@/pages/dashboard/OverviewPage'));
const DownloadsPage = lazy(() => import('@/pages/dashboard/DownloadsPage'));
const SettingsPage = lazy(() => import('@/pages/dashboard/SettingsPage'));
const ProfileCardSettingsPage = lazy(() => import('@/pages/dashboard/ProfileCardSettingsPage'));
const ReleaseManagementPage = lazy(() => import('@/pages/dashboard/ReleaseManagementPage'));
const SystemStatusPage = lazy(() => import('@/pages/dashboard/SystemStatusPage'));
const DietPage = lazy(() => import('@/pages/dashboard/DietPage'));
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'));
const AuthCallbackPage = lazy(() => import('@/features/auth/pages/AuthCallbackPage'));
const AuthErrorPage = lazy(() => import('@/features/auth/pages/AuthErrorPage'));

export function AppRouter() {
  return (
    <AuthProvider>
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/releases" element={<ReleasesPublicPage />} />
          <Route
            path="/diet/*"
            element={
              <ProtectedRoute>
                <RoleRoute minimum={AppRole.ADMIN}>
                  <DietPage />
                </RoleRoute>
              </ProtectedRoute>
            }
          />

          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/auth/error" element={<AuthErrorPage />} />

          <Route element={<AuthLayout />}>
            <Route
              path="/login"
              element={
                <GuestRoute>
                  <LoginPage />
                </GuestRoute>
              }
            />
            <Route
              path="/register"
              element={
                <GuestRoute>
                  <RegisterPage />
                </GuestRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <GuestRoute>
                  <ForgotPasswordPage />
                </GuestRoute>
              }
            />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          <Route
            element={
              <ProtectedRoute>
                <PortalShell />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<OverviewPage />} />
            <Route path="/dashboard/downloads" element={<DownloadsPage />} />
            <Route path="/dashboard/settings" element={<SettingsPage />} />
            <Route path="/dashboard/status" element={<SystemStatusPage />} />
            <Route path="/dashboard/settings/profile-card" element={<ProfileCardSettingsPage />} />
            <Route
              path="/dashboard/diet"
              element={<Navigate to="/diet" replace />}
            />
            <Route
              path="/dashboard/diet/profile"
              element={<Navigate to="/diet/library" replace />}
            />
            <Route
              path="/dashboard/diet/ingredients"
              element={<Navigate to="/diet/library" replace />}
            />
            <Route
              path="/dashboard/releases"
              element={
                <RoleRoute minimum={AppRole.OWNER}>
                  <ReleaseManagementPage />
                </RoleRoute>
              }
            />
            <Route path="/dashboard/*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}
